import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiDownload, FiFile, FiRepeat } from "react-icons/fi";
import {
  BidStatus,
  NegotiationRoundStatus,
  PortalAwardOutcomeDto,
  bffApi,
  useGetPortalMyBidsQuery,
  useGetPortalNegotiationRoundsQuery,
  type PortalDocumentDto,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { ListLoadError } from "../../../../common/ListLoadError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch, useAppSelector } from "../../../../common/redux/hooks";
import { ownPortalBid } from "../ownPortalBid";
import { isPortalRoundClosed } from "./missedPortalRound";
import { PrivateBidCta } from "./PrivateBidCta";
import { roundAcceptsBids } from "./roundAcceptsBids";
import { SectionCard } from "./SectionCard";
import { portalEventMayHaveRounds } from "./useOpenPortalRound";

interface Props {
  event: PortalSourcingEventDetailDto;
}

export const PortalNegotiationRoundsSection = ({ event }: Props) => {
  const { t } = useTranslation();

  // The server returns only the rounds this organization is invited to, so it decides who sees
  // what. A private invitee is a participant from the moment they are invited unless they decline;
  // a public one is a participant by way of the § 23-2 invitation the round was built from.
  const mayHaveRounds = portalEventMayHaveRounds(event);

  const { data, isLoading, isError, isFetching, refetch } = useGetPortalNegotiationRoundsQuery(
    { eventId: event.id },
    { skip: !mayHaveRounds },
  );
  const isLoggedInWithOrg = useAppSelector((state) => state.authState.state?.type === "LoggedInWithOrgContext");
  const { data: myBids } = useGetPortalMyBidsQuery({}, { skip: !mayHaveRounds || !isLoggedInWithOrg });
  const callerWon = ownPortalBid(myBids?.bids, event.id)?.awardOutcome === PortalAwardOutcomeDto.Winner;

  if (!mayHaveRounds) return null;

  const rounds = data?.rounds ?? [];
  const visibleRounds = rounds.filter(
    (r) => r.status === NegotiationRoundStatus.Published || r.status === NegotiationRoundStatus.Completed,
  );

  if (!isLoading && !isError && visibleRounds.length === 0) return null;

  return (
    <SectionCard icon={FiRepeat} title={t("Negotiation rounds")} count={visibleRounds.length}>
      {isLoading ? (
        <Center py="8">
          <Spinner color="smPrimary" />
        </Center>
      ) : isError ? (
        <ListLoadError message={t("Could not load negotiation rounds.")} onRetry={refetch} isRetrying={isFetching} />
      ) : (
        <Stack spacing="4">
          <LatestRoundBanner rounds={visibleRounds} event={event} />
          <Stack spacing="3">
            {visibleRounds.map((round, idx) => (
              <PortalRoundRow
                key={round.id}
                round={round}
                event={event}
                defaultExpanded={idx === visibleRounds.length - 1 && round.status === NegotiationRoundStatus.Published}
                callerWon={callerWon}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

interface LatestRoundBannerProps {
  rounds: PortalNegotiationRoundDto[];
  event: PortalSourcingEventDetailDto;
}

const LatestRoundBanner = ({ rounds, event }: LatestRoundBannerProps) => {
  const { t } = useTranslation();
  const latestPublished = rounds.filter((r) => roundAcceptsBids(r)).at(-1);

  if (!latestPublished) return null;

  return (
    <Box bg="smPrimarySofter" rounded="lg" px="4" py="3" border="1px solid" borderColor="smPrimary">
      <Flex align="center" justify="space-between" flexWrap="wrap" gap="3">
        <Stack spacing="0">
          <Text fontWeight="semibold" fontSize="sm">
            {t("Latest open round")}: {t("Round")} {latestPublished.roundNumber}
            {latestPublished.isFinalRound ? ` — ${t("Final Round")}` : ""}
          </Text>
          {latestPublished.title && (
            <Text fontSize="sm" color="smMuted">
              {latestPublished.title}
            </Text>
          )}
          {latestPublished.isFinalRound && <FinalRoundNotice />}
          {latestPublished.isCallerRejected && (
            <Text fontSize="sm" color="smMuted" data-testid="latest-round-rejected">
              {t("Your bid has been rejected, so you are no longer part of this negotiation.")}
            </Text>
          )}
        </Stack>
        <PrivateBidCta event={event} round={latestPublished} variant="compact" />
      </Flex>
    </Box>
  );
};

interface PortalRoundRowProps {
  round: PortalNegotiationRoundDto;
  event: PortalSourcingEventDetailDto;
  defaultExpanded: boolean;
  callerWon: boolean;
}

const PortalRoundRow = ({ round, event, defaultExpanded, callerWon }: PortalRoundRowProps) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const acceptsBids = roundAcceptsBids(round);
  const deadlinePassed = isPortalRoundClosed(round);
  const closedRoundLine = deadlinePassed && round.isCallerInvited ? closedRoundText(round, callerWon, t) : null;

  return (
    <Box
      border="1px solid"
      borderColor={isExpanded ? "smPrimary" : "smBorder"}
      rounded="lg"
      overflow="hidden"
      bg="smBackground"
      transition="border-color 0.15s"
    >
      <Flex
        as="button"
        type="button"
        w="full"
        textAlign="left"
        px="4"
        py="3"
        align="center"
        justify="space-between"
        cursor="pointer"
        _hover={{ bg: "smBackgroundSecondary" }}
        _focusVisible={{ boxShadow: "outline" }}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <HStack spacing="3">
          <Text fontWeight="semibold" fontSize="sm">
            {t("Round")} {round.roundNumber}
            {round.isFinalRound ? ` — ${t("Final Round")}` : ""}
          </Text>
          <RoundStatusBadge round={round} />
        </HStack>
        <HStack spacing="3">
          <Text fontSize="sm" color="smMuted">
            {t("Deadline")}: {displayDateWithTime(round.deadline)}
          </Text>
          <Icon
            as={FiChevronDown}
            boxSize="4"
            color="smMuted"
            transform={isExpanded ? "rotate(180deg)" : undefined}
            transition="transform 0.15s"
          />
        </HStack>
      </Flex>

      <Collapse in={isExpanded} animateOpacity>
        <Stack spacing="4" px="4" pb="4" pt="2" borderTop="1px solid" borderColor="smBorder">
          {round.title && (
            <Text fontWeight="medium" fontSize="sm">
              {round.title}
            </Text>
          )}
          {round.description && (
            <TipTap content={round.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
          )}
          {round.isFinalRound && acceptsBids && <FinalRoundNotice />}
          {round.inviteMessage && (
            <Box bg="smBackgroundSecondary" rounded="md" px="3" py="2">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Message from the buyer")}
              </Text>
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {round.inviteMessage}
              </Text>
            </Box>
          )}
          {round.documents.length > 0 && (
            <Stack spacing="2">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Documents")} ({round.documents.length})
              </Text>
              <RoundDocumentList documents={round.documents} />
            </Stack>
          )}
          {acceptsBids && round.isCallerInvited && (
            <Box pt="1">
              <PrivateBidCta event={event} round={round} variant="full" />
            </Box>
          )}
          {acceptsBids && round.isCallerRejected && (
            <Text fontSize="sm" color="smMuted" data-testid="round-rejected">
              {t("Your bid has been rejected, so you are no longer part of this negotiation.")}
            </Text>
          )}
          {closedRoundLine && (
            <Text fontSize="sm" color="smMuted">
              {closedRoundLine}
            </Text>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

interface RoundDocumentListProps {
  documents: PortalDocumentDto[];
}

const RoundDocumentList = ({ documents }: RoundDocumentListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trigger } = useApiError();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleDownload = async (documentId: string) => {
    setPendingId(documentId);
    try {
      const result = await dispatch(
        bffApi.endpoints.getDownloadLink.initiate({ docId: documentId }, { forceRefetch: true }),
      ).unwrap();
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      trigger(e);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Stack spacing="2">
      {documents.map((doc) => (
        <HStack
          key={doc.id}
          borderWidth="1px"
          borderColor="smBorder"
          rounded="md"
          px="3"
          py="2"
          bg="smBackground"
          justify="space-between"
          align="center"
          spacing="3"
        >
          <HStack spacing="3" align="center" flex="1" minW="0">
            <Box color="smMuted" flexShrink="0">
              <FiFile size={16} />
            </Box>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {doc.name}
            </Text>
          </HStack>
          <Button
            size="xs"
            variant="outline"
            colorScheme="gray"
            leftIcon={<FiDownload />}
            onClick={() => handleDownload(doc.id)}
            isLoading={pendingId === doc.id}
            flexShrink="0"
          >
            {t("Download")}
          </Button>
        </HStack>
      ))}
    </Stack>
  );
};

const RoundStatusBadge = ({ round }: { round: PortalNegotiationRoundDto }) => {
  const { t } = useTranslation();
  if (round.status === NegotiationRoundStatus.Published && !roundAcceptsBids(round)) {
    return (
      <Badge colorScheme="orange" fontSize="xs">
        {t("Deadline passed")}
      </Badge>
    );
  }
  const colorMap: Record<NegotiationRoundStatus, string> = {
    [NegotiationRoundStatus.Draft]: "gray",
    [NegotiationRoundStatus.Published]: "green",
    [NegotiationRoundStatus.Completed]: "blue",
  };
  const labelMap: Record<NegotiationRoundStatus, string> = {
    [NegotiationRoundStatus.Draft]: t("Draft"),
    [NegotiationRoundStatus.Published]: t("Open"),
    [NegotiationRoundStatus.Completed]: t("Closed"),
  };
  return (
    <Badge colorScheme={colorMap[round.status]} fontSize="xs">
      {labelMap[round.status]}
    </Badge>
  );
};

const FinalRoundNotice = () => {
  const { t } = useTranslation();
  return (
    <Text fontSize="sm" fontWeight="medium" color="smWarning">
      {t("This is the final round. The deadline is your last chance to revise your bid.")}
    </Text>
  );
};

const closedRoundText = (round: PortalNegotiationRoundDto, callerWon: boolean, t: TFunction): string | null => {
  if (round.myBid?.status === BidStatus.Submitted) {
    return round.myBid.confirmsStandingBid
      ? t("The deadline for this round has passed. You confirmed your current bid.")
      : t("The deadline for this round has passed. Your bid was submitted.");
  }
  if (round.declinedAt) {
    return round.missingLeavesCompetition !== false
      ? t("The deadline for this round has passed. You declined the round.")
      : t("The deadline for this round has passed. You declined the round, and your latest bid still stands.");
  }
  if (callerWon) return null;
  return round.missingLeavesCompetition !== false
    ? t("The deadline for this round has passed. You did not answer, so you are not part of the next round unless the buyer invites you again.")
    : t("The deadline for this round has passed. You did not answer, and your latest bid still stands.");
};
